import type { JSX } from "preact";
import { useState } from "preact/hooks";

import { type Session, api, can } from "../api";
import { copyText } from "../clipboard";
import { Button, Card, Empty, ErrorNote, Loading, Pill } from "../components/Ui";
import { ago, huf } from "../format";
import { useApi } from "../useApi";

interface Candidate {
  code: string;
  name: string;
  price_huf: number;
  score: number;
}

interface Suggestion {
  raw_text: string;
  occurrences: number;
  first_seen: string;
  last_seen: string;
  job_ids: string[];
  candidates: Candidate[];
  /** Az aktuális árlistával már felismeri — a kifejezés nyugodtan elvethető. */
  resolved_now: Candidate | null;
  /** Talált rá tételt, de megerősítést kér — az árajánlatban jelölve szerepel. */
  uncertain_now?: Candidate | null;
  sheet_row: Record<string, string>;
}

interface Queue {
  total_unresolved: number;
  distinct_texts: number;
  suggestions: Suggestion[];
}

/**
 * Alias-javaslatok.
 *
 * **A rendszer nem tanul magától, és ez szándékos.** Ha a fel nem ismert
 * töredékekből automatikusan aliasok lennének, egy rossz felismerés
 * véglegesítené magát, és az árazás onnantól csendben téves maradna.
 *
 * Ezért ez a nézet javaslatot ad, nem döntést: a beilleszthető sort adja
 * oda, a beírás helye a Google Sheet.
 *
 * **Elvetés** (a rendelő kérése, 2026-09-22): a javítás után a kifejezés
 * elvethető a listából. A nyomvonal megmarad; a lista onnantól csak az ÚJ
 * előfordulásokat számolja — ha a kifejezés visszajön, a javítás nem volt
 * teljes.
 */
export function ReviewView({ session }: { session: Session | null }): JSX.Element {
  const { data, error, loading, reload } = useApi<Queue>(
    "/admin/v1/review/aliases?days=90&limit=40",
  );
  const [copied, setCopied] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const javithat = can(session, "javit");

  const dismiss = async (texts: string[]): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.post<{ dismissed: number }>("/admin/v1/review/aliases/dismiss", {
        raw_texts: texts,
      });
      setMessage({
        tone: "info",
        text: `${result.dismissed} kifejezés törölve a listából. Ha újra előfordul, visszakerül.`,
      });
      reload();
    } catch (e: unknown) {
      setMessage({ tone: "bad", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const felismertek = data?.suggestions.filter((s) => s.resolved_now) ?? [];

  // A beutalón álló alakot másoljuk: az árlista „egyéb nevei” cellájának
  // végére kerül, vesszővel. (Korábban egy négyoszlopos alias-sort másolt,
  // egy olyan munkalapra, ami a rendelő árlistáján nincs.)
  const copy = async (suggestion: Suggestion, itemName: string): Promise<void> => {
    if (await copyText(suggestion.raw_text)) {
      setCopied(suggestion.raw_text);
      window.setTimeout(() => setCopied(null), 2500);
      return;
    }
    // A „vágólapon” jelzés csak akkor igaz, ha a böngésző tényleg engedte.
    // Ha nem, a teendő itt jelenik meg, kijelölhetően.
    setMessage({
      tone: "warn",
      text: `A böngésző nem engedte a másolást. Írja a(z) „${itemName}” sor „egyéb nevei” cellájának végére: ${suggestion.raw_text}`,
    });
  };

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (loading && !data) return <Loading />;

  return (
    <Card
      title="Fel nem ismert és bizonytalan kifejezések"
      hint="Gyakoriság szerint. Egy tízszer visszatérő töredék javítása tízszer annyit ér,
            mint egy egyszeri elgépelésé."
      actions={
        javithat &&
        felismertek.length > 0 && (
          <Button
            onClick={() => void dismiss(felismertek.map((s) => s.raw_text))}
            disabled={busy}
          >
            {`Már felismertek törlése (${felismertek.length})`}
          </Button>
        )
      }
    >
      {message && <p className={`notice notice--${message.tone}`}>{message.text}</p>}
      {!data?.suggestions.length ? (
        <Empty>
          Nincs feloldatlan vagy bizonytalan kifejezés az elmúlt 90 napban. Ez jó jel:
          a beutalókon szereplő megnevezések mind szerepelnek az árlistán vagy az
          aliasok között.
        </Empty>
      ) : (
        <>
          <p className="muted">
            {data.total_unresolved} feloldatlan vagy megerősítést kérő tétel,{" "}
            {data.distinct_texts} különböző kifejezés. A javítás helye az árlista{" "}
            <strong>„egyéb nevei”</strong> oszlopa — a rendszer szándékosan nem ír vissza
            az árlistába.
          </p>

          <table className="rows">
            <thead>
              <tr>
                <th>A beutalón</th>
                <th className="num">Előfordulás</th>
                <th>Legutóbb</th>
                <th>Javasolt megfeleltetés</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.suggestions.map((s) => (
                <tr key={s.raw_text}>
                  <td>
                    <strong>{s.raw_text}</strong>
                    {s.resolved_now && (
                      <>
                        <br />
                        <Pill tone="ok">már felismeri: {s.resolved_now.name}</Pill>
                      </>
                    )}
                    {s.uncertain_now && (
                      <>
                        <br />
                        <Pill tone="warn">megerősítést kér: {s.uncertain_now.name}</Pill>
                      </>
                    )}
                  </td>
                  <td className="num">{s.occurrences}×</td>
                  <td className="muted">{ago(s.last_seen)}</td>
                  <td>
                    {s.candidates.length === 0 ? (
                      <span className="muted">
                        nincs jelölt — írja be annak a tételnek az „egyéb nevei” közé,
                        amelyiket jelenti
                      </span>
                    ) : (
                      <ul className="candidates">
                        {s.candidates.map((c) => (
                          <li key={c.code}>
                            {c.name}{" "}
                            <span className="muted">
                              {huf(c.price_huf)} · {c.score.toFixed(0)} pont · ennek a sornak
                              az „egyéb nevei” közé
                            </span>
                            <Button onClick={() => void copy(s, c.name)}>Név másolása</Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td>
                    {copied === s.raw_text && <Pill tone="ok">vágólapon</Pill>}
                    {javithat && (
                      <Button tone="ghost" onClick={() => void dismiss([s.raw_text])} disabled={busy}>
                        Törlés a listából
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="muted">
            A „Név másolása” a beutalón álló alakot teszi a vágólapra: illessze a javasolt
            tétel sorában az <strong>„egyéb nevei”</strong> cella végére, vesszővel elválasztva.
            Ha a kifejezés egy vizsgálat <em>része</em> (pl. a vizeletcsík elemei a Teljes
            vizeletnek), a cellába <em>Részei: …</em> sort írjon — aki a részt kéri, az egészet
            kapja, egyszer számolva. Ha egy név tévesen, de rendszeresen erre a vizsgálatra
            áll a beutalókon, <em>Tévesen: …</em> jelöléssel vegye fel. Utána indítson
            szinkront a Szabályok nézetben, és a feldolgozásnál nyomja meg az Újrajátszást;
            ha a kifejezés mellett megjelenik a „már felismeri” jelzés, törölje a listából.
            A „megerősítést kér” jelzésű kifejezésre a rendszer talált tételt, de nem
            vállalja magától — az árajánlatban jelölve jelenik meg; ha a jelölt helyes, a név
            felvétele után biztos találat lesz. Az egybe- és különírást (<em>SeFe</em> ·{" "}
            <em>Se Fe</em>), a szérum-előtagot és a tétel nevében zárójelben álló rövidítést
            (<em>Nátrium (Na)</em> → <em>Na</em>) a rendszer magától kezeli.
          </p>
        </>
      )}
    </Card>
  );
}
